import io from 'socket.io-client';

import consts from '../consts';

const MAPA_SUBSCRIBE_MAX_BACKOFF_MS = 30000;

/** Aceita número, string ou lista — o mapa pode acompanhar vários grupos. */
function normalizaGrupos(grupos) {
  const lista = Array.isArray(grupos) ? grupos : [grupos];
  const ids = [];
  for (const g of lista) {
    const id = parseInt(g, 10);
    if (id > 0 && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.connected = false;
    this.baseURL = consts.OAPI_URL.replace('/oapi', '');
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity;
    this.isAuthenticated = false;
    this.pendingAuthData = null;
    this.authAttempted = false;
    this.lastErrorList = null;
    this._mapaFaseGrupos = new Set();
    this._mapaFaseGruposOk = new Set();
    this._mapaFaseSubscribeTimer = null;
    this._mapaFaseSubscribeAttempts = 0;

    if (typeof window !== 'undefined') {
      window.socketDebug = {
        getStatus: this.getStatus.bind(this),
        reconnect: () => {
          this.disconnect();
          this.connect();
          return 'Tentando reconectar...';
        },
        countListeners: () => {
          const counts = {};
          this.listeners.forEach((list, event) => {
            counts[event] = list.length;
          });
          return counts;
        },
      };
    }
  }

  connect() {
    if (this.socket) {
      if (!this.connected) {
        this.socket.connect();
      } else if (!this.isAuthenticated) {
        this.tryAuthenticate();
      }
      return;
    }

    this.authAttempted = false;
    this.isAuthenticated = false;

    try {
      this.socket = io(this.baseURL, {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        forceNew: true,
      });

      this.socket.on('connect', () => {
        this.connected = true;
        this.isAuthenticated = false;
        this._mapaFaseGruposOk.clear();
        this.reconnectAttempts = 0;

        if (this.pendingAuthData) {
          this.performAuthentication(this.pendingAuthData);
        } else {
          this.tryAuthenticate();
        }
      });

      this.socket.on('erros_ativos_lista', erros => {
        if (!Array.isArray(erros)) {
          console.warn('WEBSOCKET: Lista de erros inválida recebida (esperava array). Ignorando.', erros);
          return;
        }

        this.lastErrorList = erros;

        if (this.listeners.has('lista_erros_atualizada')) {
          const callbacks = [...this.listeners.get('lista_erros_atualizada')];
          callbacks.forEach(callback => {
            try {
              callback(erros, 'lista_completa');
            } catch (e) {
              console.error('WEBSOCKET: ERRO ao executar callback para lista_erros_atualizada:', e);
            }
          });
        }
      });

      this.socket.on('lista_erros_atualizada', erros => {
        if (!Array.isArray(erros)) {
          console.warn('WEBSOCKET: Lista de erros inválida recebida (esperava array). Ignorando.', erros);
          return;
        }

        this.lastErrorList = erros;

        if (this.listeners.has('lista_erros_atualizada')) {
          const callbacks = [...this.listeners.get('lista_erros_atualizada')];
          callbacks.forEach(callback => {
            try {
              callback(erros, 'lista_atualizada');
            } catch (e) {
              console.error('WEBSOCKET: ERRO ao executar callback para lista_erros_atualizada:', e);
            }
          });
        }
      });

      this.socket.on('novo_erro', erro => {
        if (!erro || typeof erro !== 'object') {
          console.warn('WEBSOCKET: Formato de erro inválido recebido. Ignorando.', erro);
          return;
        }

        if (this.listeners.has('novo_erro') && this.listeners.get('novo_erro').length > 0) {
          const callbacks = [...this.listeners.get('novo_erro')];
          callbacks.forEach(callback => {
            try {
              callback(erro, 'novo_erro');
            } catch (e) {
              console.error('WEBSOCKET: ERRO ao executar callback para novo_erro:', e);
            }
          });
        }
      });

      this.socket.on('authenticated', payload => {
        if (payload && payload.ok) {
          this.isAuthenticated = true;
          this._mapaFaseSubscribeAttempts = 0;
          this.scheduleMapaFaseSubscribe();
        } else {
          this.isAuthenticated = false;
          this.authAttempted = false;
          console.error('WEBSOCKET: autenticação rejeitada:', payload && payload.error);
        }
      });

      this.socket.on('mapa_fase_update', payload => {
        if (!payload || !Array.isArray(payload.data)) return;
        if (this.listeners.has('mapa_fase_update')) {
          const callbacks = [...this.listeners.get('mapa_fase_update')];
          callbacks.forEach(callback => {
            try {
              callback(payload, 'mapa_fase_update');
            } catch (e) {
              console.error('WEBSOCKET: ERRO em mapa_fase_update:', e);
            }
          });
        }
      });

      this.socket.on('mapa_fase_subscribed', payload => {
        if (this.listeners.has('mapa_fase_subscribed')) {
          const callbacks = [...this.listeners.get('mapa_fase_subscribed')];
          callbacks.forEach(callback => {
            try {
              callback(payload, 'mapa_fase_subscribed');
            } catch (e) {
              console.error('WEBSOCKET: ERRO em mapa_fase_subscribed:', e);
            }
          });
        }
        const grupo = parseInt(payload && payload.grupoId, 10);
        if (payload && payload.ok) {
          this._mapaFaseSubscribeAttempts = 0;
          if (grupo) this._mapaFaseGruposOk.add(grupo);
        } else if (payload && payload.error === 'nao_autenticado') {
          this.isAuthenticated = false;
          this._mapaFaseGruposOk.clear();
          this.tryAuthenticate();
          this.scheduleMapaFaseSubscribe();
        } else if (payload && (payload.error === 'sem_permissao' || payload.error === 'modulo_desligado')) {
          // Recusa definitiva — sai do alvo para não segurar o mapa em "carregando".
          if (grupo) {
            this._mapaFaseGrupos.delete(grupo);
            this._mapaFaseGruposOk.delete(grupo);
          }
        } else {
          if (grupo) this._mapaFaseGruposOk.delete(grupo);
          this.scheduleMapaFaseSubscribe();
        }
      });

      this.socket.on('disconnect', () => {
        this.connected = false;
        this.isAuthenticated = false;
        this._mapaFaseGruposOk.clear();
        this.authAttempted = false;
      });

      this.socket.on('reconnect', () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.isAuthenticated = false;
        this._mapaFaseGruposOk.clear();
        if (this.pendingAuthData) {
          this.performAuthentication(this.pendingAuthData);
        } else {
          this.tryAuthenticate();
        }
      });

      this.socket.on('reconnect_attempt', attemptNumber => {
        this.reconnectAttempts = attemptNumber;
      });

      this.socket.on('connect_error', error => {
        console.error('Erro de conexão WebSocket:', error);
      });
    } catch (error) {
      console.error('Erro ao inicializar WebSocket:', error);
    }
  }

  tryAuthenticate() {
    this.authAttempted = true;

    try {
      const userKey = '_ssat';
      const userString = localStorage.getItem(userKey);

      if (!userString) {
        this.pendingAuthData = null;
        return;
      }

      let user = null;
      try {
        user = JSON.parse(userString);
      } catch (parseError) {
        console.error(`(tryAuthenticate) Falha ao parsear JSON (${userKey}):`, parseError);
        this.pendingAuthData = null;
        return;
      }

      if (user && user.email && user.token) {
        this.pendingAuthData = { email: user.email, token: user.token };

        if (this.connected) {
          this.performAuthentication(this.pendingAuthData);
        } else if (!this.socket) {
          this.connect();
        }
      } else {
        this.pendingAuthData = null;
      }
    } catch (error) {
      console.error('(tryAuthenticate) Erro geral:', error);
      this.pendingAuthData = null;
    }
  }

  performAuthentication(authData) {
    if (!this.connected) {
      this.authAttempted = false;
      return;
    }
    if (!authData || !authData.email || !authData.token) {
      this.authAttempted = false;
      return;
    }

    try {
      this.emitAuthenticate(authData);
    } catch (error) {
      console.error('(performAuthentication) Erro ao emitir authenticate:', error);
      this.isAuthenticated = false;
      this.authAttempted = false;
    }
  }

  emitAuthenticate(authData) {
    const client = this.getSocketClient();
    this.isAuthenticated = false;
    this.socket.emit('authenticate', {
      email: authData.email,
      client,
      token: authData.token,
    });
    this.pendingAuthData = authData;
    this.authAttempted = true;
  }

  readAuthFromStorage() {
    try {
      const userString = localStorage.getItem('_ssat');
      if (!userString) return null;
      const user = JSON.parse(userString);
      if (user && user.email && user.token) {
        return { email: user.email, token: user.token };
      }
    } catch (_) {}
    return null;
  }

  /** Re-auth + subscribe — uso na rota do mapa (SPA sem F5). Aceita 1 ou N grupos. */
  forceAuthAndSubscribe(grupos) {
    const alvo = normalizaGrupos(grupos);
    if (!alvo.length) return false;

    const alvoMudou =
      alvo.length !== this._mapaFaseGrupos.size || alvo.some(g => !this._mapaFaseGrupos.has(g));

    if (alvoMudou) {
      for (const antigo of [...this._mapaFaseGrupos]) {
        if (!alvo.includes(antigo)) this.unsubscribeMapaFase(antigo);
      }
      this._mapaFaseGrupos = new Set(alvo);
      for (const ok of [...this._mapaFaseGruposOk]) {
        if (!this._mapaFaseGrupos.has(ok)) this._mapaFaseGruposOk.delete(ok);
      }
    }

    if (!this.socket) {
      this.connect();
      return false;
    }
    if (!this.connected) {
      this.socket.connect();
      return false;
    }

    if (!this.isAuthenticated) {
      const authData = this.pendingAuthData || this.readAuthFromStorage();
      if (authData) {
        this.emitAuthenticate(authData);
      } else {
        this.tryAuthenticate();
        return false;
      }
    }

    if (!this.isMapaFaseSubscribedOk()) {
      this.scheduleMapaFaseSubscribe();
      return false;
    }

    this.pingMapaFase();
    return true;
  }

  getSocketClient() {
    if (typeof window === 'undefined') return 'localhost';
    const hostname = window.location.hostname;
    return hostname.includes('localhost') ? 'localhost' : hostname.split('.')[0];
  }

  scheduleMapaFaseSubscribe() {
    if (!this._mapaFaseGrupos.size) return;
    clearTimeout(this._mapaFaseSubscribeTimer);

    const tentar = () => {
      const pendentes = [...this._mapaFaseGrupos].filter(g => !this._mapaFaseGruposOk.has(g));
      if (!pendentes.length) return;

      const emitidos = pendentes.filter(g => this.emitMapaFaseSubscribe(g));
      if (emitidos.length === pendentes.length) {
        return;
      }
      this._mapaFaseSubscribeAttempts += 1;
      const delay = Math.min(
        400 + this._mapaFaseSubscribeAttempts * 500,
        MAPA_SUBSCRIBE_MAX_BACKOFF_MS,
      );
      this._mapaFaseSubscribeTimer = setTimeout(tentar, delay);
    };

    this._mapaFaseSubscribeTimer = setTimeout(tentar, 300);
  }

  emitMapaFaseSubscribe(grupoId) {
    if (!this.socket || !this.connected || !this.isAuthenticated) return false;
    const grupo = parseInt(grupoId, 10);
    if (!grupo) return false;
    this.socket.emit('mapa_fase_subscribe', {
      grupoId: grupo,
      client: this.getSocketClient(),
    });
    return true;
  }

  subscribeMapaFase(grupos) {
    return this.forceAuthAndSubscribe(grupos);
  }

  /** CCO — garante conexão + auth + subscribe + ping (nunca desiste). */
  ensureMapaFaseLive(grupos) {
    return this.forceAuthAndSubscribe(grupos);
  }

  /** Ok só quando todos os grupos alvo confirmaram subscribe. */
  isMapaFaseSubscribedOk() {
    if (!this.connected || !this.isAuthenticated || !this._mapaFaseGrupos.size) return false;
    for (const grupo of this._mapaFaseGrupos) {
      if (!this._mapaFaseGruposOk.has(grupo)) return false;
    }
    return true;
  }

  pingMapaFase(grupoId) {
    const grupos = grupoId ? normalizaGrupos(grupoId) : [...this._mapaFaseGrupos];
    if (!grupos.length) return;
    if (!this.socket || !this.connected || !this.isAuthenticated) {
      this.ensureMapaFaseLive(grupos);
      return;
    }
    for (const grupo of grupos) {
      this.socket.emit('mapa_fase_ping', {
        grupoId: grupo,
        client: this.getSocketClient(),
      });
    }
  }

  unsubscribeMapaFase(grupoId) {
    const grupo = parseInt(grupoId, 10);
    if (!grupo) return;
    this._mapaFaseGrupos.delete(grupo);
    this._mapaFaseGruposOk.delete(grupo);
    if (!this.socket || !this.connected) return;
    this.socket.emit('mapa_fase_unsubscribe', {
      grupoId: grupo,
      client: this.getSocketClient(),
    });
  }

  unsubscribeMapaFaseTodos() {
    clearTimeout(this._mapaFaseSubscribeTimer);
    for (const grupo of [...this._mapaFaseGrupos]) {
      this.unsubscribeMapaFase(grupo);
    }
    this._mapaFaseGrupos.clear();
    this._mapaFaseGruposOk.clear();
  }

  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    this.listeners.get(event).push(callback);

    if (event === 'lista_erros_atualizada' && Array.isArray(this.lastErrorList)) {
      try {
        callback(this.lastErrorList, 'cache_replay');
      } catch (e) {
        console.error('WEBSOCKET: ERRO ao executar callback (cache_replay):', e);
      }
    }
  }

  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  getStatus() {
    const listenersCount = {};
    this.listeners.forEach((list, event) => {
      listenersCount[event] = list.length;
    });

    return {
      connected: this.connected,
      isAuthenticated: this.isAuthenticated,
      mapaFaseSubscribedOk: this.isMapaFaseSubscribedOk(),
      mapaFaseGrupos: [...this._mapaFaseGrupos],
      mapaFaseGruposOk: [...this._mapaFaseGruposOk],
      listenersCount,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket ? this.socket.id : null,
    };
  }

  disconnect() {
    clearTimeout(this._mapaFaseSubscribeTimer);
    if (this.socket) {
      this.socket.disconnect();
    }
    this.socket = null;
    this.connected = false;
    this.isAuthenticated = false;
    this._mapaFaseGrupos.clear();
    this._mapaFaseGruposOk.clear();
    this.pendingAuthData = null;
    this.authAttempted = false;
  }
}

const socketService = new SocketService();

if (typeof window !== 'undefined') {
  window.socketService = socketService;
}

export default socketService;
